import {
  PolicyFindingView,
  PolicyFindingTransitionPayload,
  PolicyVersionView as PolicyVersionSchema,
  PolicyVersionList as PolicyVersionListSchema,
  PolicyVersionContent as PolicyVersionContentSchema,
  type DecisionProofPayload,
} from "@repo/shared/policyContracts";
/**
 * Governance state for the Regulation workspace.
 *
 * Same idiom as `state/policies.ts`: per-org cached atoms over the workspace
 * server's `/api/amoreg/regulation/*` routes, which forward to the domain API
 * where the database work and the tenant guard live. Nothing here decides who
 * may see what.
 *
 * Every fetch distinguishes *unavailable* from *empty*, because in a
 * compliance product those are opposite claims: "you have no findings" and
 * "we could not reach the service that finds them" must never render the
 * same way.
 *
 * @module state/governance
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo, useState } from "react";

import { noteLocalReviewWrite, type ReviewCountUpdate } from "./reviewCountNotifications";
import { DISMISS_CATEGORIES, type DismissCategory, type FindingPlan } from "@repo/shared/findingPlanning";
import { RecordApplication } from "@repo/shared/recordUpdates";
import { ReviewCountsSchema, sameReviewCounts, type ReviewCounts } from "@repo/shared/reviewCounts";
import { complianceEventQueryAtom, complianceQueryAtom } from "./complianceLive";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import type { OrgProfileUnavailableReason } from "./orgProfile";
import { fetchComplianceJson as fetchJson } from "./complianceFetch";
import { domainApiReadPath } from "@repo/shared/domainApiRoutes";
import type { ComplianceTarget } from "./domainApi";

const FrameworkSchema = Schema.Struct({
  id: Schema.String,
  slug: Schema.String,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  version: Schema.NullOr(Schema.String),
  issuer: Schema.NullOr(Schema.String),
  jurisdiction: Schema.NullOr(Schema.String),
  domain: Schema.NullOr(Schema.String),
  iconUrl: Schema.NullOr(Schema.String),
  enabled: Schema.Boolean,
  enabledAt: Schema.NullOr(Schema.String),
  targetDate: Schema.NullOr(Schema.String),
  controlTotal: Schema.Number,
  controlsSatisfied: Schema.Number,
  /** Null when the framework has no catalogued controls — not the same as 0%. */
  percentComplete: Schema.NullOr(Schema.Number),
});
export type Framework = typeof FrameworkSchema.Type;

const CountsSchema = Schema.Struct({
  total: Schema.Number,
  published: Schema.Number,
  needingReview: Schema.Number,
});

const OverviewSchema = Schema.Struct({
  policies: CountsSchema,
  evidence: Schema.Struct({ total: Schema.Number, complete: Schema.Number }),
  documents: Schema.Struct({ total: Schema.Number, ready: Schema.Number }),
  frameworks: Schema.Array(FrameworkSchema),
});
export type RegulationOverview = typeof OverviewSchema.Type;

const FrameworkListSchema = Schema.Struct({
  frameworks: Schema.Array(FrameworkSchema),
});
export type FrameworkList = typeof FrameworkListSchema.Type;

const IntegrationScopeSchema = Schema.Struct({
  locations: Schema.Array(Schema.String),
  operations: Schema.Array(Schema.String),
});
export type IntegrationScope = typeof IntegrationScopeSchema.Type;

const IntegrationSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  source: Schema.String,
  toolkitSlug: Schema.NullOr(Schema.String),
  description: Schema.String,
  iconUrl: Schema.NullOr(Schema.String),
  /** Colour family for the connector's chips (`purple` for Slack); absent on an older API. */
  accent: Schema.optional(Schema.NullOr(Schema.String)),
  capabilities: Schema.Array(Schema.String),
  connected: Schema.Boolean,
  connectionStatus: Schema.String,
  enabled: Schema.Boolean,
  lastConnectedAt: Schema.NullOr(Schema.String),
  lastSyncedAt: Schema.NullOr(Schema.String),
  /**
   * How this integration connects: Composio OAuth, the firm's own API
   * credentials, or an admin's sign-in at the vendor under Amoreg's partner
   * credentials (Altruist). Optional so an older API interops; absent reads
   * as oauth.
   */
  contract: Schema.optional(Schema.Literals(["oauth", "api-key", "partner-oauth"])),
  /**
   * On the wire the API reports a partner sign-in as `contract: "oauth"` plus
   * this flag, so bundles that predate it still decode the list; decoding
   * folds it back into `contract: "partner-oauth"`.
   */
  partnerOAuth: Schema.optional(Schema.Boolean),
  /** Whether the org has supplied an api-key contract's credentials. */
  configured: Schema.optional(Schema.Boolean),
  /**
   * Where a document-store integration may look and what it may do there —
   * the firm's own boundary. Null when unset; absent on an older API.
   */
  scope: Schema.optional(Schema.NullOr(IntegrationScopeSchema)),
});
export type Integration = typeof IntegrationSchema.Type;

const IntegrationListSchema = Schema.Struct({
  integrations: Schema.Array(IntegrationSchema),
});
export type IntegrationList = typeof IntegrationListSchema.Type;

const PolicyGovernanceSchema = Schema.Struct({
  policyId: Schema.String,
  title: Schema.NullOr(Schema.String),
  lifecycleStatus: Schema.Literals(["draft", "in_review", "published", "archived"]),
  ownerId: Schema.NullOr(Schema.String),
  ownerEmail: Schema.NullOr(Schema.String),
  department: Schema.NullOr(Schema.String),
  reviewFrequency: Schema.Literals(["none", "monthly", "quarterly", "semiannual", "yearly"]),
  reviewDueAt: Schema.NullOr(Schema.String),
  lastReviewedAt: Schema.NullOr(Schema.String),
  publishedAt: Schema.NullOr(Schema.String),
  publishedVersion: Schema.Number,
  summary: Schema.NullOr(Schema.String),
});
export type PolicyGovernance = typeof PolicyGovernanceSchema.Type;
export type PolicyLifecycleStatus = PolicyGovernance["lifecycleStatus"];
export type PolicyReviewFrequency = PolicyGovernance["reviewFrequency"];

const MappingSchema = Schema.Struct({
  controlId: Schema.String,
  code: Schema.String,
  title: Schema.String,
  frameworkId: Schema.String,
  frameworkName: Schema.String,
  frameworkSlug: Schema.String,
  suggestedByAgent: Schema.Boolean,
});
export type PolicyMapping = typeof MappingSchema.Type;

const MappingListSchema = Schema.Struct({ mappings: Schema.Array(MappingSchema) });

const AcknowledgmentSchema = Schema.Struct({
  userId: Schema.String,
  email: Schema.NullOr(Schema.String),
  policyVersion: Schema.Number,
  acknowledgedAt: Schema.String,
});
export type PolicyAcknowledgment = typeof AcknowledgmentSchema.Type;

const AcknowledgmentListSchema = Schema.Struct({
  acknowledgments: Schema.Array(AcknowledgmentSchema),
});

const ActivitySchema = Schema.Struct({
  id: Schema.String,
  kind: Schema.String,
  detail: Schema.NullOr(Schema.String),
  actorId: Schema.NullOr(Schema.String),
  actorEmail: Schema.NullOr(Schema.String),
  /** The finding or suggestion the entry is about; absent on older servers. */
  subjectId: Schema.optionalKey(Schema.NullOr(Schema.String)),
  createdAt: Schema.String,
});
export type PolicyActivityEntry = typeof ActivitySchema.Type;

const ActivityListSchema = Schema.Struct({ activity: Schema.Array(ActivitySchema) });

const decodeOverview = Schema.decodeUnknownEffect(OverviewSchema);
const decodeFrameworks = Schema.decodeUnknownEffect(FrameworkListSchema);
/** The wire's `partnerOAuth` flag as the contract the client works with. */
export const normalizeIntegrationList = (list: IntegrationList): IntegrationList => ({
  integrations: list.integrations.map(({ partnerOAuth, ...integration }) =>
    partnerOAuth === true ? { ...integration, contract: "partner-oauth" as const } : integration,
  ),
});
const decodeIntegrations = (input: unknown) =>
  Schema.decodeUnknownEffect(IntegrationListSchema)(input).pipe(
    Effect.map(normalizeIntegrationList),
  );
const decodeGovernance = Schema.decodeUnknownEffect(Schema.NullOr(PolicyGovernanceSchema));
const decodeMappings = Schema.decodeUnknownEffect(MappingListSchema);
const decodeAcknowledgments = Schema.decodeUnknownEffect(AcknowledgmentListSchema);
const decodeActivity = Schema.decodeUnknownEffect(ActivityListSchema);

const url = (path: string, params: Record<string, string>) =>
  resolvePrimaryEnvironmentHttpUrl(`/api/amoreg/regulation/${path}`, params);

/** A read the browser may take straight to the domain API, with its proxy twin. */
const target = (path: string, params: Record<string, string>): ComplianceTarget => ({
  proxyUrl: url(path, params),
  apiPath: domainApiReadPath(path, params),
});

/** Atoms keyed by org (and policy where relevant); empty key resolves to null. */
const overviewAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("overview", { organizationId }), decodeOverview),
  ).pipe(Atom.withLabel(`web-amoreg:regulation-overview:${organizationId}`)),
);

const frameworksAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("frameworks", { organizationId }), decodeFrameworks),
  ).pipe(Atom.withLabel(`web-amoreg:frameworks:${organizationId}`)),
);

const integrationsAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("integrations", { organizationId }), decodeIntegrations),
  ).pipe(Atom.withLabel(`web-amoreg:integrations:${organizationId}`)),
);

/** Policy-scoped atoms are keyed by "org:policy" so switching either refetches. */
const policyKey = (organizationId: string, policyId: string) => `${organizationId}:${policyId}`;

const splitKey = (key: string): { organizationId: string; policyId: string } => {
  const index = key.indexOf(":");
  return index === -1
    ? { organizationId: key, policyId: "" }
    : { organizationId: key.slice(0, index), policyId: key.slice(index + 1) };
};

const policyGovernanceAtom = Atom.family((key: string) => {
  const { organizationId, policyId } = splitKey(key);
  return complianceQueryAtom(
    organizationId,
    organizationId.length === 0 || policyId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("governance", { organizationId, policyId }), decodeGovernance),
  ).pipe(Atom.withLabel(`web-amoreg:policy-governance:${key}`));
});

const policyMappingsAtom = Atom.family((key: string) => {
  const { organizationId, policyId } = splitKey(key);
  return complianceQueryAtom(
    organizationId,
    organizationId.length === 0 || policyId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("mappings", { organizationId, policyId }), decodeMappings),
  ).pipe(Atom.withLabel(`web-amoreg:policy-mappings:${key}`));
});

const policyAcknowledgmentsAtom = Atom.family((key: string) => {
  const { organizationId, policyId } = splitKey(key);
  return complianceQueryAtom(
    organizationId,
    organizationId.length === 0 || policyId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("acknowledgments", { organizationId, policyId }), decodeAcknowledgments),
  ).pipe(Atom.withLabel(`web-amoreg:policy-acknowledgments:${key}`));
});

const policyActivityAtom = Atom.family((key: string) => {
  const { organizationId, policyId } = splitKey(key);
  return complianceQueryAtom(
    organizationId,
    organizationId.length === 0 || policyId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("activity", { organizationId, policyId }), decodeActivity),
  ).pipe(Atom.withLabel(`web-amoreg:policy-activity:${key}`));
});

const readState = <A>(
  result: AsyncResult.AsyncResult<A | { unavailable: OrgProfileUnavailableReason } | null, unknown>,
) => {
  const value = AsyncResult.value(result);
  const data = value._tag === "Some" ? value.value : null;
  const unavailable =
    data !== null && typeof data === "object" && "unavailable" in data
      ? (data.unavailable as OrgProfileUnavailableReason)
      : result._tag === "Failure"
        ? ("error" as const)
        : null;
  return {
    snapshot: unavailable === null ? ((data ?? null) as A | null) : null,
    unavailableReason: unavailable,
    isPending: result.waiting,
  };
};

export interface GovernanceView<A> {
  readonly snapshot: A | null;
  readonly unavailableReason: OrgProfileUnavailableReason | null;
  readonly isPending: boolean;
  readonly refresh: () => void;
}

/**
 * The atom shape every read in this module produces: a decoded payload, an
 * explicit unavailable marker, or null before an organization is chosen.
 */
type GovernanceAtom<A> = Atom.Atom<
  AsyncResult.AsyncResult<A | { unavailable: OrgProfileUnavailableReason } | null, never>
>;

/** One hook shape for every read here — they differ only in atom and payload. */
function useGovernanceAtom<A>(atom: GovernanceAtom<A>): GovernanceView<A> {
  const result = useAtomValue(atom);
  const state = readState<A>(result);
  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);
  return { ...state, refresh };
}

export const reviewCountsResultAtom = Atom.family((organizationId: string) =>
  complianceEventQueryAtom(organizationId, (event) =>
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(
          target("review-counts", { organizationId }),
          Schema.decodeUnknownEffect(ReviewCountsSchema),
        ).pipe(Effect.map((value) => ("unavailable" in value ? value : { counts: value, event }))),
  ).pipe(Atom.withLabel(`web-amoreg:review-counts:${organizationId}`)),
);

/** Keep transport cursors and background waiting states out of the render dependency. */
export const reviewCountsViewAtom = Atom.family((organizationId: string) =>
  Atom.make((get) => {
    const state = readState<ReviewCountUpdate>(get(reviewCountsResultAtom(organizationId)));
    return {
      snapshot: state.snapshot?.counts ?? null,
      unavailableReason: state.unavailableReason,
      isPending: state.snapshot === null && state.isPending,
    };
  }).pipe(
    Atom.withEquality<Omit<GovernanceView<ReviewCounts>, "refresh">>(
      (previous, next) =>
        previous.unavailableReason === next.unavailableReason &&
        previous.isPending === next.isPending &&
        sameReviewCounts(previous.snapshot, next.snapshot),
    ),
  ),
);

/** All consumers share one read; unchanged results preserve their snapshot identity. */
export function useReviewCounts(organizationId: string | null): GovernanceView<ReviewCounts> {
  const state = useAtomValue(reviewCountsViewAtom(organizationId ?? ""));
  const refresh = useCallback(() => {
    appAtomRegistry.refresh(reviewCountsResultAtom(organizationId ?? ""));
  }, [organizationId]);
  return { ...state, refresh };
}

export function useRegulationOverview(
  organizationId: string | null,
): GovernanceView<RegulationOverview> {
  const atom = useMemo(() => overviewAtom(organizationId ?? ""), [organizationId]);
  return useGovernanceAtom<RegulationOverview>(atom);
}

export function useFrameworks(organizationId: string | null): GovernanceView<FrameworkList> {
  const atom = useMemo(() => frameworksAtom(organizationId ?? ""), [organizationId]);
  return useGovernanceAtom<FrameworkList>(atom);
}

/* ------------------------------------------------------------------ */
/* Monitors — the sources an organization watches for its frameworks   */
/* ------------------------------------------------------------------ */

const MonitorFrameworkRefSchema = Schema.Struct({
  id: Schema.String,
  slug: Schema.String,
  name: Schema.String,
});
export const MonitorAccessSchema = Schema.Literals(["fetch", "browser"]);
export const MonitorCadenceSchema = Schema.Literals(["daily", "weekly", "monthly"]);

const MonitorSchema = Schema.Struct({
  id: Schema.String,
  catalogueId: Schema.NullOr(Schema.String),
  name: Schema.String,
  issuer: Schema.String,
  jurisdictionSlug: Schema.NullOr(Schema.String),
  sourceUrls: Schema.Array(Schema.String),
  access: MonitorAccessSchema,
  cadence: MonitorCadenceSchema,
  enabled: Schema.Boolean,
  lastCheckedAt: Schema.NullOr(Schema.String),
  lastCheckNote: Schema.NullOr(Schema.String),
  frameworks: Schema.Array(MonitorFrameworkRefSchema),
  publications: Schema.Number,
  needsReview: Schema.Number,
  createdAt: Schema.String,
});
export type Monitor = typeof MonitorSchema.Type;

const SuggestedMonitorSchema = Schema.Struct({
  catalogueId: Schema.String,
  name: Schema.String,
  issuer: Schema.String,
  jurisdictionSlug: Schema.NullOr(Schema.String),
  sourceUrls: Schema.Array(Schema.String),
  access: MonitorAccessSchema,
  frameworks: Schema.Array(MonitorFrameworkRefSchema),
});
export type SuggestedMonitor = typeof SuggestedMonitorSchema.Type;

const MonitorListSchema = Schema.Struct({
  monitors: Schema.Array(MonitorSchema),
  suggested: Schema.Array(SuggestedMonitorSchema),
});
export type MonitorList = typeof MonitorListSchema.Type;
const decodeMonitors = Schema.decodeUnknownEffect(MonitorListSchema);

const monitorsAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("monitors", { organizationId }), decodeMonitors),
  ).pipe(Atom.withLabel(`web-amoreg:monitors:${organizationId}`)),
);

export interface MonitorCreateInput {
  readonly catalogueId?: string | null;
  readonly name?: string;
  readonly issuer?: string;
  readonly jurisdictionSlug?: string | null;
  readonly sourceUrls?: ReadonlyArray<string>;
  readonly access?: "fetch" | "browser";
  readonly cadence?: "daily" | "weekly" | "monthly";
  readonly frameworkIds?: ReadonlyArray<string>;
}

export interface MonitorPatchInput {
  readonly enabled?: boolean;
  readonly name?: string;
  readonly sourceUrls?: ReadonlyArray<string>;
  readonly access?: "fetch" | "browser";
  readonly cadence?: "daily" | "weekly" | "monthly";
  readonly frameworkIds?: ReadonlyArray<string>;
}

export interface MonitorsView extends GovernanceView<MonitorList> {
  /** Watch a catalogue source, or a source of your own; null on success. */
  readonly create: (input: MonitorCreateInput) => Promise<string | null>;
  readonly patch: (monitorId: string, input: MonitorPatchInput) => Promise<string | null>;
  readonly remove: (monitorId: string) => Promise<string | null>;
}

export function useMonitors(organizationId: string | null): MonitorsView {
  const atom = useMemo(() => monitorsAtom(organizationId ?? ""), [organizationId]);
  const view = useGovernanceAtom<MonitorList>(atom);
  const { refresh } = view;
  const send = useCallback(
    async (
      method: "POST" | "DELETE",
      params: Record<string, string>,
      body?: unknown,
      denied?: string,
    ) => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(url("monitors", { organizationId, ...params }), {
          method,
          credentials: "include",
          headers: { "content-type": "application/json" },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        });
        if (!response.ok) {
          if (response.status === 403)
            return denied ?? "Only organization admins can change monitors.";
          const text = await response.text().catch(() => "");
          try {
            const parsed = JSON.parse(text) as { message?: string };
            if (parsed.message) return parsed.message;
          } catch {
            // Not JSON — fall through.
          }
          return "The monitor could not be saved.";
        }
        refresh();
        return null;
      } catch {
        return "The monitor could not be saved.";
      }
    },
    [organizationId, refresh],
  );
  const create = useCallback<MonitorsView["create"]>((input) => send("POST", {}, input), [send]);
  const patch = useCallback<MonitorsView["patch"]>(
    (monitorId, input) => send("POST", { monitorId }, input),
    [send],
  );
  const remove = useCallback<MonitorsView["remove"]>(
    (monitorId) => send("DELETE", { monitorId }),
    [send],
  );
  return { ...view, create, patch, remove };
}

/* ------------------------------------------------------------------ */
/* The firm's own frameworks — the layer between regimes and documents */
/* ------------------------------------------------------------------ */

const FrameworkRegimeRefSchema = Schema.Struct({
  id: Schema.String,
  slug: Schema.String,
  name: Schema.String,
  issuer: Schema.NullOr(Schema.String),
  domain: Schema.NullOr(Schema.String),
  jurisdiction: Schema.NullOr(Schema.String),
});
const FrameworkDocumentRefSchema = Schema.Struct({
  id: Schema.String,
  filename: Schema.String,
  title: Schema.NullOr(Schema.String),
  documentKind: Schema.Literals(["policy", "procedure", "framework"]),
});
const FrameworkSourceRefSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  enabled: Schema.Boolean,
  lastCheckedAt: Schema.NullOr(Schema.String),
});
const GovernanceFrameworkSchema = Schema.Struct({
  id: Schema.String,
  slug: Schema.String,
  name: Schema.String,
  domain: Schema.NullOr(Schema.String),
  description: Schema.NullOr(Schema.String),
  ownerId: Schema.NullOr(Schema.String),
  ownerEmail: Schema.NullOr(Schema.String),
  regimes: Schema.Array(FrameworkRegimeRefSchema),
  documents: Schema.Array(FrameworkDocumentRefSchema),
  sources: Schema.Array(FrameworkSourceRefSchema),
  publicationsThisWeek: Schema.Number,
  openFindings: Schema.Number,
  createdAt: Schema.String,
});
export type GovernanceFramework = typeof GovernanceFrameworkSchema.Type;
export type FrameworkRegimeRef = typeof FrameworkRegimeRefSchema.Type;
export type FrameworkDocumentRef = typeof FrameworkDocumentRefSchema.Type;
const GovernanceFrameworkListSchema = Schema.Struct({
  frameworks: Schema.Array(GovernanceFrameworkSchema),
  // Optional so an older API still decodes; the summary falls back to a sum.
  openFindings: Schema.optionalKey(Schema.Number),
  unorganisedRegimes: Schema.Array(FrameworkRegimeRefSchema),
  unorganisedDocuments: Schema.Array(FrameworkDocumentRefSchema),
});
export type GovernanceFrameworkList = typeof GovernanceFrameworkListSchema.Type;
const decodeGovernanceFrameworks = Schema.decodeUnknownEffect(GovernanceFrameworkListSchema);

const governanceFrameworksAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("governance-frameworks", { organizationId }), decodeGovernanceFrameworks),
  ).pipe(Atom.withLabel(`web-amoreg:governance-frameworks:${organizationId}`)),
);

export interface GovernanceFrameworkInput {
  readonly name?: string;
  readonly description?: string | null;
  readonly ownerId?: string | null;
  readonly domain?: string | null;
  readonly regimeIds?: ReadonlyArray<string>;
}

export interface GovernanceFrameworksView extends GovernanceView<GovernanceFrameworkList> {
  readonly create: (input: GovernanceFrameworkInput & { name: string }) => Promise<string | null>;
  readonly patch: (frameworkId: string, input: GovernanceFrameworkInput) => Promise<string | null>;
  readonly remove: (frameworkId: string) => Promise<string | null>;
  /** Seed frameworks from the domains of the enabled regimes; null on success. */
  readonly propose: () => Promise<string | null>;
}

export function useGovernanceFrameworks(organizationId: string | null): GovernanceFrameworksView {
  const atom = useMemo(() => governanceFrameworksAtom(organizationId ?? ""), [organizationId]);
  const view = useGovernanceAtom<GovernanceFrameworkList>(atom);
  const { refresh } = view;
  const send = useCallback(
    async (method: "POST" | "DELETE", params: Record<string, string>, body?: unknown) => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(url("governance-frameworks", { organizationId, ...params }), {
          method,
          credentials: "include",
          headers: { "content-type": "application/json" },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        });
        if (!response.ok) {
          if (response.status === 403) return "Only organization admins can change frameworks.";
          const text = await response.text().catch(() => "");
          try {
            const parsed = JSON.parse(text) as { message?: string };
            if (parsed.message) return parsed.message;
          } catch {
            // Not JSON — fall through.
          }
          return "The framework could not be saved.";
        }
        refresh();
        return null;
      } catch {
        return "The framework could not be saved.";
      }
    },
    [organizationId, refresh],
  );
  const create = useCallback<GovernanceFrameworksView["create"]>(
    (input) => send("POST", {}, input),
    [send],
  );
  const patch = useCallback<GovernanceFrameworksView["patch"]>(
    (frameworkId, input) => send("POST", { frameworkId }, input),
    [send],
  );
  const remove = useCallback<GovernanceFrameworksView["remove"]>(
    (frameworkId) => send("DELETE", { frameworkId }),
    [send],
  );
  const propose = useCallback<GovernanceFrameworksView["propose"]>(
    () => send("POST", { propose: "1" }, {}),
    [send],
  );
  return { ...view, create, patch, remove, propose };
}

export interface IntegrationsView extends GovernanceView<IntegrationList> {
  /** Enable or disable for the whole organization; null on success. */
  readonly setEnabled: (connectorId: string, enabled: boolean) => Promise<string | null>;
  /** Store an api-key contract's values for the org; null on success. */
  readonly setCredentials: (
    connectorId: string,
    values: Readonly<Record<string, string>>,
  ) => Promise<string | null>;
  /** Forget the org's credentials and disconnect; null on success. */
  readonly clearCredentials: (connectorId: string) => Promise<string | null>;
  /** Set where a document-store integration may look and what it may do; null on success. */
  readonly setScope: (connectorId: string, scope: IntegrationScope) => Promise<string | null>;
}

/**
 * The `message` of a JSON error body, when the response carries one that a
 * person can act on (the credential check's verdict); null otherwise.
 */
async function rejectionMessage(response: Response): Promise<string | null> {
  if (response.status !== 400 && response.status !== 422) return null;
  try {
    const body: unknown = await response.clone().json();
    const message =
      typeof body === "object" && body !== null && "message" in body
        ? (body as { message?: unknown }).message
        : undefined;
    return typeof message === "string" && message.trim().length > 0 ? message : null;
  } catch {
    return null;
  }
}

/** One tool RuleGuard's MCP server offers the organization's service account. */
export interface IntegrationTool {
  readonly name: string;
  readonly description: string;
  /** `read` when the vendor marks the tool read-only; `write` otherwise. */
  readonly effect: "read" | "write";
  readonly effectSource: "annotation" | "default";
}

const IntegrationToolsSchema = Schema.Struct({
  connectorId: Schema.String,
  tools: Schema.Array(
    Schema.Struct({
      name: Schema.String,
      description: Schema.String,
      effect: Schema.Literals(["read", "write"]),
      effectSource: Schema.Literals(["annotation", "default"]),
    }),
  ),
});
const decodeIntegrationTools = Schema.decodeUnknownSync(IntegrationToolsSchema);

export interface IntegrationToolsView {
  /** Null until read; empty when the account has no tools. */
  readonly tools: ReadonlyArray<IntegrationTool> | null;
  readonly error: string | null;
  readonly isPending: boolean;
  readonly refresh: () => void;
}

/**
 * The live tool catalogue behind an integration that has one (RuleGuard):
 * the workspace signs in as the org's stored service account and relays what
 * the vendor lists. Null organization = nothing to read (no credential yet).
 */
export function useIntegrationTools(
  organizationId: string | null,
  connectorId: string,
): IntegrationToolsView {
  const [state, setState] = useState<{
    key: string;
    tools: ReadonlyArray<IntegrationTool> | null;
    error: string | null;
  }>({ key: "", tools: null, error: null });
  const [revision, setRevision] = useState(0);
  const key = organizationId === null ? "" : `${organizationId}:${connectorId}:${revision}`;
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    if (organizationId === null) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch(url("integration-tools", { organizationId, connectorId }), {
          credentials: "include",
          signal: controller.signal,
        });
        if (!response.ok) {
          const reason = (await rejectionMessage(response)) ?? (await errorField(response));
          if (!controller.signal.aborted) {
            setState({
              key,
              tools: null,
              error: reason ?? "The tool catalogue could not be read.",
            });
          }
          return;
        }
        const decoded = decodeIntegrationTools(await response.json());
        if (!controller.signal.aborted) setState({ key, tools: decoded.tools, error: null });
      } catch {
        if (!controller.signal.aborted) {
          setState({ key, tools: null, error: "The tool catalogue could not be read." });
        }
      }
    })();
    return () => controller.abort();
  }, [organizationId, connectorId, key]);
  const current = state.key === key ? state : { tools: null, error: null };
  return {
    tools: organizationId === null ? null : current.tools,
    error: organizationId === null ? null : current.error,
    isPending: organizationId !== null && current.tools === null && current.error === null,
    refresh,
  };
}

/** The `error` of a JSON error body, for reads that answer that way. */
async function errorField(response: Response): Promise<string | null> {
  try {
    const body: unknown = await response.clone().json();
    const error =
      typeof body === "object" && body !== null && "error" in body
        ? (body as { error?: unknown }).error
        : undefined;
    return typeof error === "string" && error.trim().length > 0 ? error : null;
  } catch {
    return null;
  }
}

/** The colour family an integration's catalogue row names; an empty org key loads nothing. */
export function useIntegrationAccentFamily(
  organizationId: string | null,
  connectorId: string,
): string | null {
  const atom = useMemo(() => integrationsAtom(organizationId ?? ""), [organizationId]);
  const view = useGovernanceAtom<IntegrationList>(atom);
  const integration = view.snapshot?.integrations.find((item) => item.id === connectorId);
  return integration?.accent ?? null;
}

export function useIntegrations(organizationId: string | null): IntegrationsView {
  const atom = useMemo(() => integrationsAtom(organizationId ?? ""), [organizationId]);
  const view = useGovernanceAtom<IntegrationList>(atom);
  const setEnabled = useCallback(
    async (connectorId: string, enabled: boolean): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(url("integrations", { organizationId, connectorId }), {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ enabled }),
        });
        if (!response.ok) {
          return enabled
            ? "The integration could not be connected."
            : "The integration could not be disconnected.";
        }
        view.refresh();
        return null;
      } catch {
        return "The integration could not be updated.";
      }
    },
    [organizationId, view],
  );
  const setCredentials = useCallback(
    async (
      connectorId: string,
      values: Readonly<Record<string, string>>,
    ): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(
          url("integration-credentials", { organizationId, connectorId }),
          {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ values }),
          },
        );
        if (!response.ok) {
          // The workspace checks a credential against the vendor before it
          // is stored; when that fails, its reason is the useful message.
          return (await rejectionMessage(response)) ?? "The credentials could not be saved.";
        }
        view.refresh();
        return null;
      } catch {
        return "The credentials could not be saved.";
      }
    },
    [organizationId, view],
  );
  const clearCredentials = useCallback(
    async (connectorId: string): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(
          url("integration-credentials", { organizationId, connectorId }),
          { method: "DELETE", credentials: "include" },
        );
        if (!response.ok) {
          return "The integration could not be disconnected.";
        }
        view.refresh();
        return null;
      } catch {
        return "The integration could not be disconnected.";
      }
    },
    [organizationId, view],
  );
  const setScope = useCallback(
    async (connectorId: string, scope: IntegrationScope): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(url("integration-scope", { organizationId, connectorId }), {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(scope),
        });
        if (!response.ok) {
          return "The scope could not be saved.";
        }
        view.refresh();
        return null;
      } catch {
        return "The scope could not be saved.";
      }
    },
    [organizationId, view],
  );
  return { ...view, setEnabled, setCredentials, clearCredentials, setScope };
}

export interface PolicyGovernanceView extends GovernanceView<PolicyGovernance> {
  /** Save governance fields; resolves to an error message, or null on success. */
  readonly save: (
    input: Partial<{
      title: string | null;
      lifecycleStatus: PolicyLifecycleStatus;
      ownerId: string | null;
      department: string | null;
      reviewFrequency: PolicyReviewFrequency;
      reviewDueAt: string | null;
      summary: string | null;
      /** The firm's own framework to file the document under; null to unfile. */
      frameworkId: string | null;
    }>,
  ) => Promise<string | null>;
}

export function usePolicyGovernance(
  organizationId: string | null,
  policyId: string,
): PolicyGovernanceView {
  const atom = useMemo(
    () => policyGovernanceAtom(policyKey(organizationId ?? "", policyId)),
    [organizationId, policyId],
  );
  const view = useGovernanceAtom<PolicyGovernance>(atom);
  const save = useCallback(
    async (input: Parameters<PolicyGovernanceView["save"]>[0]): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(url("governance", { organizationId, policyId }), {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(input),
        });
        if (!response.ok) {
          return response.status === 403
            ? "Only organization admins can change policy settings."
            : "The policy settings could not be saved.";
        }
        view.refresh();
        return null;
      } catch {
        return "The policy settings could not be saved.";
      }
    },
    [organizationId, policyId, view],
  );
  return { ...view, save };
}

export function usePolicyMappings(organizationId: string | null, policyId: string) {
  const atom = useMemo(
    () => policyMappingsAtom(policyKey(organizationId ?? "", policyId)),
    [organizationId, policyId],
  );
  return useGovernanceAtom<{ mappings: ReadonlyArray<PolicyMapping> }>(atom);
}

export function usePolicyAcknowledgments(organizationId: string | null, policyId: string) {
  const atom = useMemo(
    () => policyAcknowledgmentsAtom(policyKey(organizationId ?? "", policyId)),
    [organizationId, policyId],
  );
  return useGovernanceAtom<{
    acknowledgments: ReadonlyArray<PolicyAcknowledgment>;
  }>(atom);
}

export function usePolicyActivity(organizationId: string | null, policyId: string) {
  const atom = useMemo(
    () => policyActivityAtom(policyKey(organizationId ?? "", policyId)),
    [organizationId, policyId],
  );
  return useGovernanceAtom<{ activity: ReadonlyArray<PolicyActivityEntry> }>(atom);
}

const TimelineBucketSchema = Schema.Struct({
  month: Schema.String,
  findings: Schema.Number,
  amendments: Schema.Number,
});
export type TimelineBucket = typeof TimelineBucketSchema.Type;

const TimelineSchema = Schema.Struct({
  buckets: Schema.Array(TimelineBucketSchema),
});
export type RegulationTimeline = typeof TimelineSchema.Type;

export type PolicyVersionMeta = typeof PolicyVersionSchema.Type;

export type PolicyVersionContent = typeof PolicyVersionContentSchema.Type;

const decodeTimeline = Schema.decodeUnknownEffect(TimelineSchema);
const decodeVersionList = Schema.decodeUnknownEffect(PolicyVersionListSchema);

const timelineAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("timeline", { organizationId }), decodeTimeline),
  ).pipe(Atom.withLabel(`web-amoreg:regulation-timeline:${organizationId}`)),
);

const policyVersionsAtom = Atom.family((key: string) => {
  const { organizationId, policyId } = splitKey(key);
  return complianceQueryAtom(
    organizationId,
    organizationId.length === 0 || policyId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("versions", { organizationId, policyId }), decodeVersionList),
  ).pipe(Atom.withLabel(`web-amoreg:policy-versions:${key}`));
});

export function useRegulationTimeline(
  organizationId: string | null,
): GovernanceView<RegulationTimeline> {
  const atom = useMemo(() => timelineAtom(organizationId ?? ""), [organizationId]);
  return useGovernanceAtom<RegulationTimeline>(atom);
}

export interface PolicyVersionsView extends GovernanceView<{
  versions: ReadonlyArray<PolicyVersionMeta>;
}> {
  /** Freeze the current text as the next version; null on success. */
  readonly capture: (note: string | null) => Promise<string | null>;
  /** Fetch one version's full text; null when it cannot be loaded. */
  readonly loadContent: (version: number) => Promise<PolicyVersionContent | null>;
}

export function usePolicyVersions(
  organizationId: string | null,
  policyId: string,
): PolicyVersionsView {
  const atom = useMemo(
    () => policyVersionsAtom(policyKey(organizationId ?? "", policyId)),
    [organizationId, policyId],
  );
  const view = useGovernanceAtom<{ versions: ReadonlyArray<PolicyVersionMeta> }>(atom);

  const capture = useCallback(
    async (note: string | null): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(url("versions", { organizationId, policyId }), {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ note }),
        });
        if (!response.ok) {
          if (response.status === 400) {
            return "This document has no extracted text yet.";
          }
          return response.status === 403
            ? "Only organization admins can capture versions."
            : "The version could not be captured.";
        }
        view.refresh();
        return null;
      } catch {
        return "The version could not be captured.";
      }
    },
    [organizationId, policyId, view],
  );

  const loadContent = useCallback(
    async (version: number): Promise<PolicyVersionContent | null> => {
      if (!organizationId) return null;
      try {
        const response = await fetch(
          url("versions", { organizationId, policyId, version: String(version) }),
          { credentials: "include" },
        );
        if (!response.ok) return null;
        const json: unknown = await response.json();
        return await Effect.runPromise(
          Schema.decodeUnknownEffect(PolicyVersionContentSchema)(json),
        );
      } catch {
        return null;
      }
    },
    [organizationId, policyId],
  );

  return { ...view, capture, loadContent };
}

const OrgGovernanceSchema = Schema.Struct({
  policyId: Schema.String,
  title: Schema.NullOr(Schema.String),
  lifecycleStatus: Schema.Literals(["draft", "in_review", "published", "archived"]),
  ownerId: Schema.NullOr(Schema.String),
  ownerEmail: Schema.NullOr(Schema.String),
  department: Schema.NullOr(Schema.String),
  reviewFrequency: Schema.Literals(["none", "monthly", "quarterly", "semiannual", "yearly"]),
  reviewDueAt: Schema.NullOr(Schema.String),
  publishedAt: Schema.NullOr(Schema.String),
  publishedVersion: Schema.Number,
});
export type OrgPolicyGovernance = typeof OrgGovernanceSchema.Type;

const OrgGovernanceListSchema = Schema.Struct({
  policies: Schema.Array(OrgGovernanceSchema),
});

const decodeOrgGovernance = Schema.decodeUnknownEffect(OrgGovernanceListSchema);

const orgGovernanceAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("governance-list", { organizationId }), decodeOrgGovernance),
  ).pipe(Atom.withLabel(`web-amoreg:governance-list:${organizationId}`)),
);

/** Governance rows for every policy in the org, keyed for the library table. */
export function useOrgGovernance(organizationId: string | null): GovernanceView<{
  policies: ReadonlyArray<OrgPolicyGovernance>;
}> & { readonly byPolicyId: ReadonlyMap<string, OrgPolicyGovernance> } {
  const atom = useMemo(() => orgGovernanceAtom(organizationId ?? ""), [organizationId]);
  const view = useGovernanceAtom<{ policies: ReadonlyArray<OrgPolicyGovernance> }>(atom);
  const byPolicyId = useMemo(
    () => new Map((view.snapshot?.policies ?? []).map((row) => [row.policyId, row])),
    [view.snapshot],
  );
  return { ...view, byPolicyId };
}

const PolicyTextSchema = Schema.Struct({
  contentMarkdown: Schema.NullOr(Schema.String),
});
export type PolicyText = typeof PolicyTextSchema.Type;

const decodePolicyText = Schema.decodeUnknownEffect(PolicyTextSchema);

const policyTextAtom = Atom.family((key: string) => {
  const { organizationId, policyId } = splitKey(key);
  return complianceQueryAtom(
    organizationId,
    organizationId.length === 0 || policyId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("text", { organizationId, policyId }), decodePolicyText),
  ).pipe(Atom.withLabel(`web-amoreg:policy-text:${key}`));
});

/** The policy's current text, assembled live from its extracted sections. */
export function usePolicyText(
  organizationId: string | null,
  policyId: string,
): GovernanceView<PolicyText> {
  const atom = useMemo(
    () => policyTextAtom(policyKey(organizationId ?? "", policyId)),
    [organizationId, policyId],
  );
  return useGovernanceAtom<PolicyText>(atom);
}

/** Post a comment on a policy; resolves to an error message or null. */
export function usePolicyComment(
  organizationId: string | null,
  policyId: string,
  onPosted: () => void,
): (text: string) => Promise<string | null> {
  return useCallback(
    async (text: string) => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(url("comments", { organizationId, policyId }), {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ text }),
        });
        if (!response.ok) {
          return "The comment could not be posted.";
        }
        onPosted();
        return null;
      } catch {
        return "The comment could not be posted.";
      }
    },
    [organizationId, policyId, onPosted],
  );
}

/* ------------------------------------------------------------------ */
/* Findings on a policy                                                */
/* ------------------------------------------------------------------ */

const PolicyFindingSchema = Schema.Struct({
  ...PolicyFindingView.fields,
  decisionProof: Schema.optionalKey(PolicyFindingView.fields.decisionProof),
});
export type PolicyFinding = typeof PolicyFindingSchema.Type;
export type PolicyFindingStatus = PolicyFinding["status"];

const PolicyFindingListSchema = Schema.Struct({
  findings: Schema.Array(PolicyFindingSchema),
});
const decodeFindingList = Schema.decodeUnknownEffect(PolicyFindingListSchema);

const policyFindingsAtom = Atom.family((key: string) => {
  const { organizationId, policyId } = splitKey(key);
  return complianceQueryAtom(
    organizationId,
    organizationId.length === 0 || policyId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("findings", { organizationId, policyId }), decodeFindingList),
  ).pipe(Atom.withLabel(`web-amoreg:policy-findings:${key}`));
});

export interface PolicyFindingsView extends GovernanceView<{
  findings: ReadonlyArray<PolicyFinding>;
}> {
  /**
   * Move a finding along: resolve it by a version, close it
   * with a reason, or reopen it. Resolves to an error message, or null.
   */
  readonly transition: (
    findingId: string,
    input: {
      readonly status: PolicyFindingStatus;
      readonly resolvedVersion?: number | null;
      readonly note?: string | null;
      readonly proof?: DecisionProofUpload;
      /** Why a closure dismisses it. */
      readonly dismissCategory?: DismissCategory;
    },
  ) => Promise<string | null>;
}

/** Resolve, close (dismiss) or reopen one finding. Resolves to an error message, or null. */
export async function transitionFinding(input: {
  readonly organizationId: string;
  readonly policyId: string;
  readonly findingId: string;
  readonly status: PolicyFindingStatus;
  readonly resolvedVersion?: number | null;
  readonly note?: string | null;
  readonly proof?: DecisionProofUpload;
  readonly dismissCategory?: DismissCategory;
}): Promise<string | null> {
  const { organizationId, policyId, findingId } = input;
  noteLocalReviewWrite();
  try {
    const response = await fetch(url("findings", { organizationId, policyId, findingId }), {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        status: input.status,
        resolvedVersion: input.resolvedVersion ?? null,
        note: input.note ?? null,
        ...(input.proof === undefined ? {} : { proof: input.proof }),
        ...(input.dismissCategory === undefined ? {} : { dismissCategory: input.dismissCategory }),
      } satisfies typeof PolicyFindingTransitionPayload.Type),
    });
    if (!response.ok) {
      if (response.status === 403) return "Only organization admins can change findings.";
      const body = await response.text().catch(() => "");
      try {
        const parsed = JSON.parse(body) as { message?: string };
        if (parsed.message) return parsed.message;
      } catch {
        // Not JSON — fall through to the generic message.
      }
      return "The finding could not be updated.";
    }
    return null;
  } catch {
    return "The finding could not be updated.";
  }
}

export function usePolicyFindings(
  organizationId: string | null,
  policyId: string,
): PolicyFindingsView {
  const atom = useMemo(
    () => policyFindingsAtom(policyKey(organizationId ?? "", policyId)),
    [organizationId, policyId],
  );
  const view = useGovernanceAtom<{ findings: ReadonlyArray<PolicyFinding> }>(atom);
  const { refresh } = view;

  const transition = useCallback<PolicyFindingsView["transition"]>(
    async (findingId, input) => {
      if (!organizationId) return "No organization selected.";
      const error = await transitionFinding({ organizationId, policyId, findingId, ...input });
      if (error === null) refresh();
      return error;
    },
    [organizationId, policyId, refresh],
  );

  return { ...view, transition };
}

/* ------------------------------------------------------------------ */
/* People, owners, recommendations                                     */
/* ------------------------------------------------------------------ */

const PersonSchema = Schema.Struct({
  userId: Schema.String,
  email: Schema.String,
  name: Schema.NullOr(Schema.String),
  avatarUrl: Schema.NullOr(Schema.String),
  jobTitle: Schema.NullOr(Schema.String),
  department: Schema.NullOr(Schema.String),
  role: Schema.String,
  responsibility: Schema.optional(Schema.NullOr(Schema.String)),
});
export type OrganizationPerson = typeof PersonSchema.Type;

const PeopleListSchema = Schema.Struct({ people: Schema.Array(PersonSchema) });
const decodePeople = Schema.decodeUnknownEffect(PeopleListSchema);

const RecommendationSchema = Schema.Struct({
  id: Schema.String,
  policyId: Schema.NullOr(Schema.String),
  policyName: Schema.NullOr(Schema.String),
  kind: Schema.Literals([
    "rename",
    "review_cadence",
    "owner",
    "department",
    "jurisdictions",
    "reclassify",
    "control_mapping",
    "control_amendment",
    "missing_document",
    "framework",
    "org_context",
  ]),
  title: Schema.String,
  rationale: Schema.String,
  proposedChange: Schema.Record(Schema.String, Schema.Unknown),
  severity: Schema.NullOr(Schema.String),
  status: Schema.Literals(["open", "accepted", "applied", "dismissed"]),
  source: Schema.Literals(["inspector", "agent", "manual"]),
  decisionNote: Schema.NullOr(Schema.String),
  decidedByEmail: Schema.NullOr(Schema.String),
  decidedAt: Schema.NullOr(Schema.String),
  /** Absent on older servers; the evidence a closed amendment or gap rests on. */
  resolvedVersion: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  resolvedPolicyId: Schema.optionalKey(Schema.NullOr(Schema.String)),
  resolvedPolicyName: Schema.optionalKey(Schema.NullOr(Schema.String)),
  /** A file attached to the decision; absent on older servers. */
  decisionProof: Schema.optionalKey(Schema.NullOr(Schema.Struct({ filename: Schema.String }))),
  /** Phase-1 fields; absent on older servers (unassigned, undated, no undo). */
  dismissCategory: Schema.optionalKey(Schema.NullOr(Schema.Literals(DISMISS_CATEGORIES))),
  assigneeId: Schema.optionalKey(Schema.NullOr(Schema.String)),
  dueDate: Schema.optionalKey(Schema.NullOr(Schema.String)),
  appliedRecord: Schema.optionalKey(Schema.NullOr(RecordApplication)),
  createdAt: Schema.String,
});
export type Recommendation = typeof RecommendationSchema.Type;
export type RecommendationKind = Recommendation["kind"];

const RecommendationListSchema = Schema.Struct({
  recommendations: Schema.Array(RecommendationSchema),
});
const decodeRecommendations = Schema.decodeUnknownEffect(RecommendationListSchema);

const orgPeopleAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("people", { organizationId }), decodePeople),
  ).pipe(Atom.withLabel(`web-amoreg:org-people:${organizationId}`)),
);

const policyOwnersAtom = Atom.family((key: string) => {
  const { organizationId, policyId } = splitKey(key);
  return complianceQueryAtom(
    organizationId,
    organizationId.length === 0 || policyId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("owners", { organizationId, policyId }), decodePeople),
  ).pipe(Atom.withLabel(`web-amoreg:policy-owners:${key}`));
});

const recommendationsAtom = Atom.family((key: string) => {
  const { organizationId, policyId } = splitKey(key);
  return complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(
          target("recommendations", {
            organizationId,
            ...(policyId.length === 0 ? {} : { policyId }),
          }),
          decodeRecommendations,
        ),
  ).pipe(Atom.withLabel(`web-amoreg:recommendations:${key}`));
});

const orgFindingsAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("findings", { organizationId }), decodeFindingList),
  ).pipe(Atom.withLabel(`web-amoreg:org-findings:${organizationId}`)),
);

const OwnersMapSchema = Schema.Struct({
  owners: Schema.Array(
    Schema.Struct({ policyId: Schema.String, people: Schema.Array(PersonSchema) }),
  ),
});
const decodeOwnersMap = Schema.decodeUnknownEffect(OwnersMapSchema);

const orgPolicyOwnersAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(target("policy-owners", { organizationId }), decodeOwnersMap),
  ).pipe(Atom.withLabel(`web-amoreg:org-policy-owners:${organizationId}`)),
);

/** Owners of every document, keyed by policy id. */
export function useOwnersByPolicy(organizationId: string | null): {
  readonly byPolicyId: ReadonlyMap<string, ReadonlyArray<OrganizationPerson>>;
  readonly isPending: boolean;
  readonly unavailableReason: OrgProfileUnavailableReason | null;
  readonly ready: boolean;
  readonly refresh: () => void;
} {
  const atom = useMemo(() => orgPolicyOwnersAtom(organizationId ?? ""), [organizationId]);
  const view = useGovernanceAtom<{
    owners: ReadonlyArray<{ policyId: string; people: ReadonlyArray<OrganizationPerson> }>;
  }>(atom);
  const byPolicyId = useMemo(
    () => new Map((view.snapshot?.owners ?? []).map((row) => [row.policyId, row.people])),
    [view.snapshot],
  );
  return {
    byPolicyId,
    isPending: view.isPending,
    unavailableReason: view.unavailableReason,
    ready: view.snapshot !== null,
    refresh: view.refresh,
  };
}

/** Everyone who could own a document here. */
export function useOrganizationPeople(
  organizationId: string | null,
): GovernanceView<{ people: ReadonlyArray<OrganizationPerson> }> {
  const atom = useMemo(() => orgPeopleAtom(organizationId ?? ""), [organizationId]);
  return useGovernanceAtom<{ people: ReadonlyArray<OrganizationPerson> }>(atom);
}

export interface PolicyOwnersView extends GovernanceView<{
  people: ReadonlyArray<OrganizationPerson>;
}> {
  /** Replace the owner set; resolves to an error message, or null. */
  readonly save: (userIds: ReadonlyArray<string>) => Promise<string | null>;
}

export function usePolicyOwners(organizationId: string | null, policyId: string): PolicyOwnersView {
  const atom = useMemo(
    () => policyOwnersAtom(policyKey(organizationId ?? "", policyId)),
    [organizationId, policyId],
  );
  const view = useGovernanceAtom<{ people: ReadonlyArray<OrganizationPerson> }>(atom);
  const { refresh } = view;
  const save = useCallback(
    async (userIds: ReadonlyArray<string>) => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(url("owners", { organizationId, policyId }), {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ userIds: [...userIds] }),
        });
        if (!response.ok) {
          return response.status === 403
            ? "Only organization admins can change owners."
            : "The owners could not be saved.";
        }
        refresh();
        return null;
      } catch {
        return "The owners could not be saved.";
      }
    },
    [organizationId, policyId, refresh],
  );
  return { ...view, save };
}

/** Every open finding in the org, for library-level counts. */
export function useOrganizationFindings(
  organizationId: string | null,
): GovernanceView<{ findings: ReadonlyArray<PolicyFinding> }> {
  const atom = useMemo(() => orgFindingsAtom(organizationId ?? ""), [organizationId]);
  return useGovernanceAtom<{ findings: ReadonlyArray<PolicyFinding> }>(atom);
}

export interface RecommendationsView extends GovernanceView<{
  recommendations: ReadonlyArray<Recommendation>;
}> {
  /**
   * apply / dismiss (reason required) / reopen. Applying an
   * amendment names the version that carried it; applying a missing
   * document names the document that was uploaded — the server refuses
   * either without its evidence.
   */
  readonly decide: (
    recommendationId: string,
    decision: "apply" | "dismiss" | "reopen" | "undo",
    options?: RecommendationDecisionOptions,
  ) => Promise<string | null>;
}

export interface RecommendationDecisionOptions {
  /** Why a dismissal dismisses a gap. */
  readonly dismissCategory?: DismissCategory;
  readonly note?: string | null;
  readonly resolvedVersion?: number;
  readonly resolvedPolicyId?: string;
  /** Optional supporting file for the decision. */
  readonly proof?: DecisionProofUpload;
}

export type DecisionProofUpload = DecisionProofPayload;

export function useRecommendations(
  organizationId: string | null,
  policyId?: string,
): RecommendationsView {
  const atom = useMemo(
    () => recommendationsAtom(policyKey(organizationId ?? "", policyId ?? "")),
    [organizationId, policyId],
  );
  const view = useGovernanceAtom<{ recommendations: ReadonlyArray<Recommendation> }>(atom);
  const { refresh } = view;
  const decide = useCallback<RecommendationsView["decide"]>(
    async (recommendationId, decision, options) => {
      if (!organizationId) return "No organization selected.";
      noteLocalReviewWrite();
      try {
        const response = await fetch(url("recommendations", { organizationId, recommendationId }), {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            decision,
            note: options?.note ?? null,
            ...(options?.resolvedVersion === undefined
              ? {}
              : { resolvedVersion: options.resolvedVersion }),
            ...(options?.resolvedPolicyId === undefined
              ? {}
              : { resolvedPolicyId: options.resolvedPolicyId }),
            ...(options?.proof === undefined ? {} : { proof: options.proof }),
            ...(options?.dismissCategory === undefined ? {} : { dismissCategory: options.dismissCategory }),
          }),
        });
        if (!response.ok) {
          if (response.status === 403)
            return "Only organization admins can decide recommendations.";
          const body = await response.text().catch(() => "");
          try {
            const parsed = JSON.parse(body) as { message?: string };
            if (parsed.message) return parsed.message;
          } catch {
            // Not JSON — fall through.
          }
          return "The recommendation could not be updated.";
        }
        refresh();
        return null;
      } catch {
        return "The recommendation could not be updated.";
      }
    },
    [organizationId, refresh],
  );
  return { ...view, decide };
}

/** The API's message from a refused write, or null. */
async function refusalMessage(response: Response): Promise<string | null> {
  const body = await response.text().catch(() => "");
  try {
    const parsed = JSON.parse(body) as { message?: string };
    return parsed.message ?? null;
  } catch {
    return null;
  }
}

/**
 * Assign a finding or set its due date. A regulatory finding is addressed by
 * its document; a gap suggestion by its own id. Resolves to an error message,
 * or null. The caller refreshes what it shows.
 */
export async function planFinding(input: {
  readonly organizationId: string;
  readonly target:
    | { readonly origin: "finding"; readonly policyId: string; readonly id: string }
    | { readonly origin: "suggestion"; readonly id: string };
  readonly plan: FindingPlan;
}): Promise<string | null> {
  const { organizationId, target, plan } = input;
  const address =
    target.origin === "finding"
      ? url("findings", { organizationId, policyId: target.policyId, findingId: target.id })
      : url("recommendations", { organizationId, recommendationId: target.id });
  noteLocalReviewWrite();
  try {
    const response = await fetch(address, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ plan }),
    });
    if (response.ok) return null;
    if (response.status === 403) return "Only organization admins can assign findings.";
    return (await refusalMessage(response)) ?? "The finding could not be updated.";
  } catch {
    return "The finding could not be updated.";
  }
}

/** Move a document between the policy and procedure shelves. */
export async function saveDocumentKind(input: {
  readonly organizationId: string;
  readonly policyId: string;
  readonly documentKind: "policy" | "procedure";
  readonly implementsPolicyId?: string | null;
}): Promise<string | null> {
  try {
    const response = await fetch(
      url("kind", { organizationId: input.organizationId, policyId: input.policyId }),
      {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          documentKind: input.documentKind,
          implementsPolicyId: input.implementsPolicyId ?? null,
        }),
      },
    );
    if (!response.ok) {
      return response.status === 403
        ? "Only organization admins can move documents."
        : "The document could not be moved.";
    }
    return null;
  } catch {
    return "The document could not be moved.";
  }
}

const InspectionActivitySchema = Schema.Struct({
  from: Schema.String,
  to: Schema.String,
  total: Schema.Number,
  days: Schema.Array(Schema.Struct({ date: Schema.String, count: Schema.Number })),
});
export type InspectionActivitySnapshot = typeof InspectionActivitySchema.Type;
const decodeInspectionActivity = Schema.decodeUnknownEffect(InspectionActivitySchema);

const inspectionActivityAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(
          resolvePrimaryEnvironmentHttpUrl("/api/amoreg/inspector/activity", { organizationId }),
          decodeInspectionActivity,
        ),
  ).pipe(Atom.withLabel(`web-amoreg:inspection-activity:${organizationId}`)),
);

/** Shared cursor refresh plus a sequential safety refresh for runs with no governance writes. */
export function useInspectionActivity(
  organizationId: string | null,
): GovernanceView<InspectionActivitySnapshot> {
  const atom = useMemo(() => inspectionActivityAtom(organizationId ?? ""), [organizationId]);
  const view = useGovernanceAtom<InspectionActivitySnapshot>(atom);
  const { isPending, refresh, snapshot, unavailableReason } = view;
  useEffect(() => {
    if (!organizationId || isPending) return;
    const timer = setTimeout(refresh, 15_000);
    return () => clearTimeout(timer);
  }, [organizationId, isPending, refresh, snapshot, unavailableReason]);
  return view;
}

/** The same cached reads used by the compliance overview and its activity card. */
export function complianceOverviewGovernanceAtoms(
  organizationId: string,
): ReadonlyArray<Atom.Atom<AsyncResult.AsyncResult<unknown, unknown>>> {
  return [
    overviewAtom(organizationId),
    reviewCountsResultAtom(organizationId),
    timelineAtom(organizationId),
    orgGovernanceAtom(organizationId),
    orgPolicyOwnersAtom(organizationId),
    recommendationsAtom(policyKey(organizationId, "")),
    orgFindingsAtom(organizationId),
    inspectionActivityAtom(organizationId),
  ];
}
